<template>
  <div class="m-section summary-band">
    <div class="m-section__header">
      <div class="m-section__headline">Resumen de tu cocina</div>
    </div>

    <div class="row">
      <div class="col-md-4 col-12 mb-3 mb-md-0">
        <div class="summary-card" @click="goTo(DAILY_LEVELS_PATH)">
          <div class="summary-card__title">Nivel Menú Diario</div>
          <div v-if="isLoadingDailyMenu" class="summary-card__placeholder">Cargando…</div>
          <template v-else>
            <div class="summary-card__value">{{ dailyMenuLevel }}</div>
            <div class="summary-card__meta">{{ dailyMenuPeriod }}</div>
          </template>
        </div>
      </div>

      <div v-if="isOnDemand" class="col-md-4 col-12 mb-3 mb-md-0">
        <div class="summary-card" @click="goTo(ON_DEMAND_LEVELS_PATH)">
          <div class="summary-card__title">Nivel On Demand</div>
          <div v-if="isLoadingOnDemand" class="summary-card__placeholder">Cargando…</div>
          <template v-else>
            <div class="summary-card__value">{{ onDemandLevel }}</div>
            <div class="summary-card__meta">{{ onDemandPeriod }}</div>
          </template>
        </div>
      </div>

      <div class="col-md-4 col-12">
        <div
          class="summary-card"
          :class="{ 'summary-card--static': !isAuditsLinkEnabled }"
          @click="goToAudits"
        >
          <div class="summary-card__title">Última auditoría</div>
          <div v-if="isLoadingAudits" class="summary-card__placeholder">Cargando…</div>
          <template v-else-if="isNotConfigured">
            <div class="summary-card__value summary-card__value--empty">—</div>
            <div class="summary-card__meta">No disponible en tu portal</div>
          </template>
          <template v-else-if="isUnavailable">
            <div class="summary-card__value summary-card__value--empty">—</div>
            <div class="summary-card__meta summary-card__alert">No se pudo consultar</div>
          </template>
          <template v-else-if="latestAudit === null">
            <div class="summary-card__value summary-card__value--empty">—</div>
            <div class="summary-card__meta">Sin auditorías con resultado</div>
          </template>
          <template v-else>
            <div class="summary-card__row">
              <span class="summary-card__value">{{ latestAudit.score }}%</span>
              <span class="summary-card__badge" :style="scaleBadgeStyle">
                {{ latestAudit.escala }}
              </span>
            </div>
            <div class="summary-card__meta">
              {{ latestAuditDate }}
              <span v-if="activeInterventionsCount > 0" class="summary-card__alert">
                · {{ activeInterventionsCount }}
                {{ activeInterventionsCount === 1 ? 'intervención activa' : 'intervenciones activas' }}
              </span>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import { estiloEscala } from '@/views/Auditorias/escalas'

const DAY_MONTH_FORMAT = 'DD/MM'
const FULL_DATE_FORMAT = 'DD/MM/YYYY'
const ISO_DATE_FORMAT = 'YYYY-MM-DD'
const AUDITS_ROUTE = 'auditorias'
const DAILY_LEVELS_PATH = '/niveles'
const ON_DEMAND_LEVELS_PATH = '/foodcourt/niveles'
const NO_VALUE = '—'

export default {
  name: 'SummaryBand',
  data () {
    return {
      DAILY_LEVELS_PATH,
      ON_DEMAND_LEVELS_PATH
    }
  },
  computed: {
    ...mapGetters(['isOnDemand']),
    ...mapGetters('metrics', ['getStatistics']),
    ...mapGetters('foodcourtLevels', ['getPeriods', 'getLastClosedRatedPeriod']),
    ...mapGetters('auditorias', [
      'isLoading',
      'isNotConfigured',
      'isUnavailable',
      'getLatestAudit',
      'getActiveInterventions'
    ]),

    isLoadingDailyMenu () {
      return this.getStatistics === null
    },

    isLoadingOnDemand () {
      return this.getPeriods === null
    },

    isLoadingAudits () {
      return this.isLoading
    },

    isAuditsLinkEnabled () {
      return !this.isLoading && !this.isNotConfigured && !this.isUnavailable
    },

    dailyMenuLevel () {
      if (this.getStatistics === null) return NO_VALUE
      return this.getStatistics.level || NO_VALUE
    },

    dailyMenuPeriod () {
      if (this.getStatistics === null) return ''
      const parts = []
      if (this.getStatistics.labelDate) parts.push('Del ' + this.getStatistics.labelDate)
      if (this.getStatistics.week) parts.push(this.getStatistics.week)
      return parts.join(' | ')
    },

    onDemandLevel () {
      const period = this.getLastClosedRatedPeriod
      if (period === null) return NO_VALUE
      return period.level || NO_VALUE
    },

    onDemandPeriod () {
      const period = this.getLastClosedRatedPeriod
      if (period === null) return 'Sin quincena calificada aún'
      return (
        'Del ' +
        moment(period.start_date).format(DAY_MONTH_FORMAT) +
        ' al ' +
        moment(period.end_date).format(DAY_MONTH_FORMAT)
      )
    },

    latestAudit () {
      return this.getLatestAudit
    },

    activeInterventionsCount () {
      return this.getActiveInterventions.length
    },

    scaleBadgeStyle () {
      if (this.latestAudit === null) return {}
      const style = estiloEscala(this.latestAudit.escala)
      return { background: style.background, color: style.color }
    },

    latestAuditDate () {
      if (this.latestAudit === null || !this.latestAudit.fecha) return ''
      const raw = this.latestAudit.fecha
      const dateOnly = raw.indexOf('T') === -1 ? raw : raw.slice(0, 10)
      const parsed = moment(dateOnly, ISO_DATE_FORMAT)
      return parsed.isValid() ? parsed.format(FULL_DATE_FORMAT) : raw
    }
  },
  methods: {
    goTo (path) {
      this.$router.push(path)
    },
    goToAudits () {
      if (!this.isAuditsLinkEnabled) {
        return
      }
      this.$router.push({ name: AUDITS_ROUTE })
    }
  }
}
</script>

<style lang="scss" scoped>
.summary-band {
  margin-bottom: 0.5rem;
}

.summary-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 16px 18px;
  height: 100%;
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: #27884d;
    box-shadow: 0 4px 12px rgba(39, 136, 77, 0.12);
  }

  &--static {
    cursor: default;

    &:hover {
      border-color: #e9ecef;
      box-shadow: none;
    }
  }

  &__title {
    font-size: 13px;
    color: #8898aa;
    margin-bottom: 6px;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  &__value {
    font-size: 28px;
    font-weight: 600;
    line-height: 1.1;
    color: #32325d;

    &--empty {
      color: #adb5bd;
    }
  }

  &__badge {
    font-size: 12px;
    font-weight: 600;
    padding: 4px 12px;
    border-radius: 20px;
    white-space: nowrap;
  }

  &__meta {
    font-size: 12px;
    color: #8898aa;
    margin-top: 4px;
  }

  &__alert {
    color: #a06800;
    font-weight: 600;
  }

  &__placeholder {
    font-size: 14px;
    color: #adb5bd;
    padding: 8px 0;
  }
}
</style>
